/**
 * Zoom utilities for calculating adaptive zoom scales based on annotation dimensions
 */

import { COORDINATE_SPACE } from "./math";

/**
 * Calculate the bounding box (min/max extents) from an array of points
 */
export function calculatePointsBoundingBox(points: { x: number; y: number }[]): {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  width: number;
  height: number;
} {
  if (points.length === 0) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 };
  }

  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

/**
 * Calculate adaptive zoom scale based on annotation dimensions in normalized space (0-1000).
 * Returns a scale that fits the annotation in the viewport with padding for labels/decorations.
 *
 * @param width - Width in normalized coordinates (0-1000)
 * @param height - Height in normalized coordinates (0-1000)
 * @param paddingFactor - Factor to leave room for labels/decorations (default 0.7)
 * @param minScale - Minimum zoom scale (default 1.0)
 * @param maxScale - Maximum zoom scale (default 2.0)
 */
export function calculateZoomScaleForDimensions(
  width: number,
  height: number,
  paddingFactor: number = 0.7,
  minScale: number = 1.0,
  maxScale: number = 2.0,
): number {
  // Convert from normalized (0-1000) to percentage (0-100)
  const widthPercent = width / (COORDINATE_SPACE / 100);
  const heightPercent = height / (COORDINATE_SPACE / 100);

  // Find the larger dimension
  const maxDimension = Math.max(widthPercent, heightPercent);

  // Avoid division by zero for point-like annotations
  if (maxDimension === 0) {
    return maxScale;
  }

  // Calculate scale that would fit the annotation with padding
  const calculatedScale = (100 / maxDimension) * paddingFactor;

  // Clamp between min and max
  return Math.max(minScale, Math.min(maxScale, calculatedScale));
}
